import axios from "axios";

import Api from "../services/AxiosService";
import { unwrap } from "./envelope";

// svc-04 `/v1/admin/landing-content*` — admin CMS for the customer Selector
// landing page (gated by canManageUsers). Mirrors the Postman "Landing Content
// Admin" folder. Types are kept local per the existing api/*.ts convention.
//
// The tree is normalised: each category owns ordered `paragraphs` / `bullets`
// (plain-text fragments) and `images` rows. Category POST/PUT accepts those
// arrays for one aggregate save; the per-resource PUT endpoints remain
// available for compatible direct child replacement.

export interface LandingMeta {
  id: number;
  version: string | null;
  generatedFrom: string | null;
  generatedAt: string | null;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  deactivatedAt: string | null;
  deactivatedBy: string | null;
}

export interface LandingParagraph {
  categoryKey: string;
  sortOrder: number;
  paragraphText: string | null;
}

export interface LandingBullet {
  categoryKey: string;
  sortOrder: number;
  bulletText: string | null;
}

export interface LandingImage {
  categoryKey: string;
  sortOrder: number;
  productId: string | null;
  /** Stored reference: an S3 key (uploaded) or a legacy external URL. */
  imageUrl: string | null;
  /** Response-only presigned URL for <img> display (S3 keys); external URLs pass through. */
  imageDisplayUrl?: string | null;
  caption: string | null;
}

/** Result of `POST /v1/admin/landing-content/images/upload`. */
export interface UploadLandingImageResult {
  /** Durable S3 key to persist as the row's `imageUrl`. */
  key: string;
  /** Short-lived presigned URL for immediate preview. */
  displayUrl: string;
}

export interface LandingCategory {
  categoryKey: string;
  title: string | null;
  subtitle: string | null;
  tip: string | null;
  /** Stored reference: an S3 key (`landing-videos/…`, uploaded) or a legacy URL. */
  videoUrl: string | null;
  /** Response-only presigned URL for <video> preview (S3 keys); URLs pass through. */
  videoDisplayUrl?: string | null;
  sortOrder: number | null;
  paragraphs: LandingParagraph[];
  bullets: LandingBullet[];
  images: LandingImage[];
}

export interface LandingTree {
  meta: LandingMeta | null;
  categories: LandingCategory[];
}

export interface CreateCategoryBody {
  categoryKey: string;
  title?: string | null;
  subtitle?: string | null;
  tip?: string | null;
  videoUrl?: string | null;
  sortOrder?: number | null;
  paragraphs?: ParagraphItem[];
  bullets?: BulletItem[];
  images?: ImageItem[];
}

export interface UpdateCategoryBody {
  title?: string | null;
  subtitle?: string | null;
  tip?: string | null;
  videoUrl?: string | null;
  sortOrder?: number | null;
  paragraphs?: ParagraphItem[];
  bullets?: BulletItem[];
  images?: ImageItem[];
}

export interface ParagraphItem {
  paragraphText: string;
}
export interface BulletItem {
  bulletText: string;
}
export interface ImageItem {
  productId?: string | null;
  imageUrl: string;
  caption?: string | null;
}

const BASE = "/v1/admin/landing-content";

/** GET /v1/admin/landing-content?includeDeactivated= — full admin tree. */
export async function getAdminLandingTree(
  includeDeactivated = false,
): Promise<LandingTree> {
  const res = await Api.get(BASE, { params: { includeDeactivated } });
  return unwrap<LandingTree>(res.data);
}

/** POST /v1/admin/landing-content/categories — create a category. */
export async function createLandingCategory(
  body: CreateCategoryBody,
): Promise<LandingCategory> {
  const res = await Api.post(`${BASE}/categories`, body);
  return unwrap<LandingCategory>(res.data);
}

/** PUT /v1/admin/landing-content/categories/:key — update / reactivate. */
export async function updateLandingCategory(
  key: string,
  body: UpdateCategoryBody,
): Promise<LandingCategory> {
  const res = await Api.put(
    `${BASE}/categories/${encodeURIComponent(key)}`,
    body,
  );
  return unwrap<LandingCategory>(res.data);
}

/** DELETE /v1/admin/landing-content/categories/:key — cascade soft-delete. */
export async function deleteLandingCategory(key: string): Promise<void> {
  await Api.delete(`${BASE}/categories/${encodeURIComponent(key)}`);
}

/** PUT .../categories/:key/paragraphs — bulk-replace ordered paragraphs. */
export async function replaceLandingParagraphs(
  key: string,
  items: ParagraphItem[],
): Promise<unknown> {
  const res = await Api.put(
    `${BASE}/categories/${encodeURIComponent(key)}/paragraphs`,
    { items },
  );
  return unwrap<unknown>(res.data);
}

/** PUT .../categories/:key/bullets — bulk-replace ordered bullets. */
export async function replaceLandingBullets(
  key: string,
  items: BulletItem[],
): Promise<unknown> {
  const res = await Api.put(
    `${BASE}/categories/${encodeURIComponent(key)}/bullets`,
    { items },
  );
  return unwrap<unknown>(res.data);
}

/** PUT .../categories/:key/images — bulk-replace ordered images. */
export async function replaceLandingImages(
  key: string,
  items: ImageItem[],
): Promise<unknown> {
  const res = await Api.put(
    `${BASE}/categories/${encodeURIComponent(key)}/images`,
    { items },
  );
  return unwrap<unknown>(res.data);
}

/**
 * POST .../images/upload — multipart upload of one image to S3 (mirrors the 3D
 * CAD flow). Returns the durable `key` (persist as `imageUrl`) + a presigned
 * `displayUrl` for immediate preview. Let the browser set the multipart
 * boundary — do NOT hand-set Content-Type.
 */
export async function uploadLandingImage(
  file: File,
): Promise<UploadLandingImageResult> {
  const form = new FormData();
  form.append("file", file);
  const res = await Api.post(`${BASE}/images/upload`, form);
  return unwrap<UploadLandingImageResult>(res.data);
}

/** 300 MB hard cap on a single hero video (mirrors the svc-04 server limit). */
export const LANDING_VIDEO_MAX_BYTES = 300 * 1024 * 1024;

/** Result of `POST .../videos/upload-url` — presigned direct-to-S3 PUT target. */
export interface LandingVideoUploadUrlResult {
  /** Durable S3 key to persist as the category's `videoUrl`. */
  key: string;
  /** Short-lived presigned PUT URL the browser uploads the bytes to. */
  uploadUrl: string;
  /** Short-lived presigned GET URL for immediate <video> preview. */
  displayUrl: string;
}

/** POST .../videos/upload-url — ask svc-04 to mint a presigned PUT target. */
export async function requestLandingVideoUploadUrl(input: {
  fileName: string;
  contentType: string;
  sizeBytes: number;
}): Promise<LandingVideoUploadUrlResult> {
  const res = await Api.post(`${BASE}/videos/upload-url`, input);
  return unwrap<LandingVideoUploadUrlResult>(res.data);
}

/**
 * Two-step direct-to-S3 hero video upload (300 MB cap):
 *   1. Ask svc-04 for a presigned PUT URL (`requestLandingVideoUploadUrl`).
 *   2. PUT the raw bytes STRAIGHT to S3 with a bare axios call — no Penn auth
 *      header, no gateway, no `Api` interceptors. The `Content-Type` MUST match
 *      what was signed or S3 rejects with a signature mismatch.
 * Returns the durable `key` (persist as `videoUrl`) + a presigned `displayUrl`.
 */
export async function uploadLandingVideo(
  file: File,
): Promise<{ key: string; displayUrl: string }> {
  const contentType = file.type || "application/octet-stream";
  const { key, uploadUrl, displayUrl } = await requestLandingVideoUploadUrl({
    fileName: file.name,
    contentType,
    sizeBytes: file.size,
  });
  await axios.put(uploadUrl, file, {
    headers: { "Content-Type": contentType },
    timeout: 0,
  });
  return { key, displayUrl };
}
